import { useMemo } from "react";
import { alpha, useTheme } from "@mui/material/styles";

/** Maps react-date-range (light-only CSS) to the active MUI palette. */
export function useDateRangeThemeSx() {
  const theme = useTheme();

  return useMemo(
    () => ({
      colorScheme: theme.palette.mode,
      "& .rdrCalendarWrapper": {
        fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.palette.background.paper,
      },
      "& .rdrDateDisplayWrapper": {
        backgroundColor: theme.palette.background.default,
      },
      "& .rdrDateDisplayItem": {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
        borderColor: theme.palette.divider,
      },
      "& .rdrDateDisplayItem input": {
        color: theme.palette.text.secondary,
      },
      "& .rdrDateDisplayItemActive input": {
        color: theme.palette.text.primary,
      },
      "& .rdrMonthAndYearWrapper": {
        color: theme.palette.text.primary,
      },
      "& .rdrMonthAndYearPickers select": {
        color: theme.palette.text.primary,
        ...(theme.palette.mode === "dark" && {
          backgroundImage: `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="9" height="6" viewBox="0 0 9 6"><path fill="rgba(255,255,255,0.7)" d="M0 0l4.5 6L9 0z"/></svg>',
          )}")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 8px center",
        }),
      },
      "& .rdrMonthAndYearPickers select:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "& .rdrNextPrevButton": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.grey[800]
            : theme.palette.grey[200],
      },
      "& .rdrNextPrevButton:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.grey[700]
            : theme.palette.grey[300],
      },
      "& .rdrPprevButton i": {
        borderColor: `transparent ${theme.palette.text.primary} transparent transparent`,
      },
      "& .rdrNextButton i": {
        borderColor: `transparent transparent transparent ${theme.palette.text.primary}`,
      },
      "& .rdrWeekDay": {
        color: theme.palette.text.secondary,
      },
      "& .rdrMonthName": {
        color: theme.palette.text.secondary,
      },
      // Days from the neighboring month are drawn in the same grid. Keep them
      // muted, but let a click select them so a range can cross the month
      // without paging (e.g. 26 Oct → 1 Nov).
      "& .rdrDayPassive:not(.rdrDayDisabled)": {
        pointerEvents: "auto",
        cursor: "pointer",
      },
      // Two months side by side both draw the shared week. Hide the copy that
      // the other month already shows; keep an overflow day when that month
      // is not on screen, so it can still be selected.
      "& .rdrMonths .rdrMonth:not(:last-child) .rdrDayEndOfMonth ~ .rdrDayPassive, & .rdrMonths .rdrMonth:not(:first-child) .rdrDayPassive:has(~ .rdrDay:not(.rdrDayPassive))":
        {
          visibility: "hidden",
          pointerEvents: "none",
        },
      "& .rdrDayPassive .rdrInRange, & .rdrDayPassive .rdrStartEdge, & .rdrDayPassive .rdrEndEdge, & .rdrDayPassive .rdrSelected, & .rdrDayPassive .rdrDayStartPreview, & .rdrDayPassive .rdrDayInPreview, & .rdrDayPassive .rdrDayEndPreview":
        {
          display: "block",
        },
      "& .rdrDayPassive .rdrInRange ~ .rdrDayNumber span, & .rdrDayPassive .rdrStartEdge ~ .rdrDayNumber span, & .rdrDayPassive .rdrEndEdge ~ .rdrDayNumber span, & .rdrDayPassive .rdrSelected ~ .rdrDayNumber span":
        {
          color: theme.palette.primary.contrastText,
        },
      "& .rdrDayPassive.rdrDayToday .rdrInRange ~ .rdrDayNumber span:after, & .rdrDayPassive.rdrDayToday .rdrStartEdge ~ .rdrDayNumber span:after, & .rdrDayPassive.rdrDayToday .rdrEndEdge ~ .rdrDayNumber span:after, & .rdrDayPassive.rdrDayToday .rdrSelected ~ .rdrDayNumber span:after":
        {
          background: theme.palette.primary.contrastText,
        },
      "& .rdrDayPassive .rdrDayNumber span": {
        color: theme.palette.text.disabled,
      },
      "& .rdrDayDisabled": {
        backgroundColor: theme.palette.action.hover,
      },
      "& .rdrDayDisabled .rdrDayNumber span": {
        color: theme.palette.text.disabled,
      },
      "& .rdrDayStartPreview, & .rdrDayInPreview, & .rdrDayEndPreview": {
        background: alpha(theme.palette.primary.main, 0.2),
        borderColor: theme.palette.primary.main,
      },
      "& .rdrDayToday .rdrDayNumber span:after": {
        background: theme.palette.primary.main,
      },
      ...(theme.palette.mode === "dark" && {
        "& .rdrDay:not(.rdrDayPassive):not(.rdrDayDisabled) .rdrDayNumber > span":
          {
            color: `${theme.palette.text.primary} !important`,
          },
        "& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, & .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, & .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span, & .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span":
          {
            color: `${theme.palette.primary.contrastText} !important`,
          },
        "& .rdrDayToday:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span:after, & .rdrDayToday:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span:after, & .rdrDayToday:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span:after, & .rdrDayToday:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span:after":
          {
            background: `${theme.palette.primary.contrastText} !important`,
          },
      }),
    }),
    [theme],
  );
}
